'use client';

import { useTranslations } from 'next-intl';
import React, { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Effect, GenerationSource } from '@/.gql/graphql';
import ShotikIcon from '@/public/icons/shotik.svg';
import { useStyleContext } from '@/src/providers';
import { usePromoCheck } from '@/src/hooks/usePromo';
import { usePrice } from '@/src/hooks/usePrice';
import { getGenerationType } from '@/src/utils/getGenerationType';

interface GenerateButtonProps {
  disabled: boolean;
  storyLinesLength?: number;
  onClick: () => Promise<void>;
}

const styles = {
  buttonTextDisabled: 'text-label-secondary',
  buttonIcon: 'absolute left-5 w-5.5',
  button:
    'sticky bottom-10 w-full left-5 right-5 bg-fill-button rounded-xl py-5 mt-8 block cursor-pointer',
  buttonVideo: 'py-4',
  buttonDisabled: 'bg-fill-toast cursor-not-allowed',
  buttonText: 'text-label-black font-extrabold text-base text-center',
  price: 'block font-light text-sm whitespace-pre-line',
  iconWrap:
    'absolute right-5 top-[50%] translate-y-[-50%] flex gap-0.5 items-center',
  icon: 'w-5 h-5 text-label-black',
};

export const GenerateButton: FC<GenerateButtonProps> = ({
  disabled,
  onClick,
  storyLinesLength,
}) => {
  const { activeStyle } = useStyleContext();
  const t = useTranslations('imageModal');

  // Определяем тип генерации для проверки промо
  const generationType = useMemo(
    () => getGenerationType(activeStyle),
    [activeStyle],
  );

  const { promoCheck } = usePromoCheck(generationType);

  const buttonText = useMemo(() => {
    switch (activeStyle?.__typename) {
      case 'Style':
        return t('button');
      case 'PhotoSession':
        return t('buttonPhotoshoot');
      case 'Effect':
        return (activeStyle as Effect)?.isStickerPack
          ? t('buttonStickerpack')
          : t('buttonEffect');
      case 'VideoLook':
      case 'VideoStyle':
      case 'CombiVideo':
      case 'VideoVeo3':
        return t('buttonVideostyle');
      case 'StoryLine':
        return t('buttonStoryline');
      default:
        return;
    }
  }, [activeStyle, t]);

  const { serviceType, serviceKey } = useMemo(() => {
    const typeName = activeStyle?.__typename;

    switch (typeName) {
      case 'Style':
        return {
          serviceType: 'image' as const,
          serviceKey: 'PRESET',
        };
      case 'PhotoSession':
        return activeStyle?.generationSource === GenerationSource.Photo
          ? {
              serviceType: 'image' as const,
              serviceKey: 'PHOTO_SESSION_TO_PHOTO',
            }
          : {
              serviceType: 'image' as const,
              serviceKey: 'PHOTO_SESSION',
            };
      case 'Effect':
        return {
          serviceType: 'image' as const,
          serviceKey: 'EFFECT_MODE',
        };
      case 'VideoLook':
        return {
          serviceType: 'video' as const,
          serviceKey: 'LOOK',
        };
      case 'VideoStyle':
        return {
          serviceType: 'video' as const,
          serviceKey: 'STYLE',
        };
      case 'CombiVideo':
        return {
          serviceType: 'video' as const,
          serviceKey: 'COMBI',
        };
      case 'VideoVeo3':
        return {
          serviceType: 'video' as const,
          serviceKey: 'VEO3',
        };
      case 'StoryLine':
        return {
          serviceType: 'video' as const,
          serviceKey: 'storyline',
        };
      default:
        return {
          serviceType: 'image' as const,
          serviceKey: 'PRESET',
        };
    }
  }, [activeStyle]);

  const finalPrice = usePrice(serviceType, serviceKey);

  const priceToShow = useMemo(() => {
    if (promoCheck.isFree) return '0';

    switch (activeStyle?.__typename) {
      case 'StoryLine':
        return storyLinesLength ? String(storyLinesLength * 15) : '15';

      case 'Effect':
        return activeStyle.isStickerPack
          ? '6'
          : String(activeStyle.priceGeneration);

      case 'Style':
        if (!finalPrice) return;
        return String(finalPrice.isFree ? 0 : finalPrice.price * 2);

      default:
        if (!finalPrice) return;
        return String(finalPrice.isFree ? 0 : finalPrice.price);
    }
  }, [
    promoCheck.isFree,
    finalPrice,
    activeStyle?.__typename,
    (activeStyle as Effect)?.isStickerPack,
    (activeStyle as Effect)?.priceGeneration,
    storyLinesLength,
  ]);

  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={twMerge(
        styles.button,
        ['VideoLook', 'VideoStyle'].includes(activeStyle?.__typename!) &&
          styles.buttonVideo,
        disabled && styles.buttonDisabled,
      )}
    >
      <div
        className={twMerge(
          styles.buttonText,
          disabled && styles.buttonTextDisabled,
        )}
      >
        {buttonText}
      </div>
      {priceToShow && (
        <div className={styles.iconWrap}>
          <div
            className={twMerge(
              styles.buttonText,
              disabled && styles.buttonTextDisabled,
            )}
          >
            {priceToShow}
          </div>

          <ShotikIcon
            className={twMerge(
              styles.icon,
              disabled && styles.buttonTextDisabled,
            )}
          />
        </div>
      )}
    </button>
  );
};
